import { createSelector } from 'reselect'
import { historyPrice, properties, leasesOnly, listings, leases, badDebts, credits } from './entities'
import { incomeStatementByPropertyID, taxDeferalByPropertyID, equityByPropertyID, depreciationByPropertyID, taxBracket, perfomancePeriod, startMarketDateByPropertyID } from './perfomance'
import { transactionsEx } from './transactions'
import { selectedProperty } from './common'
import potentialRent from 'helpers/kpi/potentialRent'
import {equityProperty} from 'helpers/kpi/equity'
import daysForRent from 'helpers/kpi/daysForRent'
import occupancyRate from 'helpers/kpi/occupancyRate'
import breakdown from 'helpers/kpi/breakdown'
import newRoi from 'helpers/kpi/newRoi'
import roiWithGain from 'helpers/kpi/roiWithGain'
import badDebt from 'helpers/kpi/badDebt'
import creditsByPropertyID from 'helpers/kpi/creditsByPropertyID'
import investment from 'helpers/kpi/investment'
import Numeral from 'numeral'
import * as R from 'ramda'
import moment from 'moment'
import { parsePeriod } from 'utils/stuff'

let netIncomeToolTip = createSelector(
	[incomeStatementByPropertyID, selectedProperty]
  , (incomeStatement, property) => {
		if(!property) return
		let rentIncome = incomeStatement.rentIncome[property]
		let expenses = incomeStatement.expenses[property] && incomeStatement.expenses[property].total
		return {
			'calc': `$${Numeral(rentIncome).format('0,0')} (Rent Income) - $${Numeral(expenses).format('0,0')} (Expenses)`
		}
	}
)

let rentIncomeTooltip = createSelector(
	[incomeStatementByPropertyID, leasesOnly, listings, properties, badDebts, credits, perfomancePeriod, startMarketDateByPropertyID, selectedProperty]
  , (incomeStatement, leasesOnly, listings, properties, badDebts, credits, perfomancePeriod, startMarketDateByPropertyID, property) => {
		if(!property) property = 'total'
		let pendingRent = incomeStatement.pendingRent[property] || 0
		let rentIncome = incomeStatement.rentIncome[property] || 0
		let potential = potentialRent(leasesOnly, listings, properties, startMarketDateByPropertyID, perfomancePeriod)[property]
		let propertyBadDebt = badDebt(badDebts, properties, perfomancePeriod)[property]
		let propertyCredits = creditsByPropertyID(credits, properties, perfomancePeriod)[property]
		let loss = potential - pendingRent - rentIncome
		return {
			'Fully Occupied Rent': `$${Numeral(potential).format('0,0')}`
		  , 'Actual Rent Collected': `$${Numeral(rentIncome + pendingRent).format('0,0')}`
		  , 'Pending Rent': `$${Numeral(0).format('0,0')}`
		  , 'Credits': `$${Numeral(propertyCredits).format('0,0')}`
		  , 'Bad Debt': `$${Numeral(propertyBadDebt).format('0,0')}`
		  , 'Unoccupied Rent Loss': `$${Numeral(loss < 0 ? 0 : loss).format('0,0')}`
		}
	}
)

let expensesTooltip = createSelector(
	[incomeStatementByPropertyID, selectedProperty]
  , (incomeStatement, property) => {
		if(!property) property = 'total'
		let expensesCategories = incomeStatement.expenses[property] && incomeStatement.expenses[property].categories
		if(expensesCategories){
			return R.pipe(R.groupBy(R.prop('category')), R.map(x => '$'+ Numeral(x[0].amount).format('0,0')))(expensesCategories)
		}
		return {'calc': 'N/A'}
	}
)

let valueGainTooltip = createSelector(
	[properties, historyPrice, selectedProperty]
  , (properties,historyPrice, property) => {
		if(!property) return
		let equity = equityProperty(historyPrice, properties)
		let propertyEquity = equity[property]
		if(propertyEquity){
			return {
				'calc': {
					"current value": {
						'first': `$${Numeral(propertyEquity.equity).format('0,0')}`
					}
				  , "Value on 31 Dec 2016": {
						"-": `$${Numeral((propertyEquity.gain - propertyEquity.equity)* -1).format('0,0')}`
					}
				  , "total": {
						"total": `$${Numeral(propertyEquity.gain).format('0,0')}`
					}
				}
			}
		}
		return {'calc': 'N/A'}

	}
)

let loanPaidTooltip = createSelector(
	[properties, perfomancePeriod, startMarketDateByPropertyID, selectedProperty]
  , (properties, perfomancePeriod, startMarketDateByPropertyID, property) => {
		if(!property) property = 'total'
		let period = perfomancePeriod == 'projected' ? parsePeriod(moment().year()) : parsePeriod(perfomancePeriod)
		let startPeriod = moment(period.from) > moment(startMarketDateByPropertyID[property]) ? period.from : startMarketDateByPropertyID[property]
		let periodStartBreakDown = breakdown(properties, period.from)[property] || {total: {}}
		let periodEndBreakDown = breakdown(properties, period.to)[property] || {total: {}}

		let periodStartPrincipalLeft = periodStartBreakDown.balance
		let periodEndPricipalLeft = periodEndBreakDown.balance

		let paidInPeriod = periodStartPrincipalLeft - periodEndPricipalLeft
		let principalLeft = periodStartPrincipalLeft - paidInPeriod

		return {
			"calc": {
				["Pricipal on " + moment(startPeriod).format("DD MMM YYYY")]: {
					"first": `$${Numeral(periodStartPrincipalLeft).format('0,0')}`
				}
			  , "Paid in period": {
					"-": `$${Numeral(paidInPeriod).format('0,0')}`
				}
			  , "Current loan principal": {
					"total": `$${Numeral(principalLeft).format('0,0')}`
				}
			}
		}
  }
)

let taxDeferalTooltip = createSelector(
	[depreciationByPropertyID, taxDeferalByPropertyID, taxBracket, selectedProperty, incomeStatementByPropertyID]
  , (depreciation, taxDeferral, taxBracket, property, incomeStatement) => {
		if(!property) return
		let taxPercents = taxBracket + "%"
		let expensesCategories = incomeStatement.expenses[property] && incomeStatement.expenses[property].categories
		let cleaning = expensesCategories && expensesCategories[0] && expensesCategories[0].amount || false
		let repair = expensesCategories && expensesCategories[3] && expensesCategories[3].amount || false
		let expenses = cleaning + repair || 'N/A'

		return {
			"calc": {
				"Depreciation": {
					"first": `$${Numeral(depreciation[property]).format('0,0')}`
				}
				, "Repair": {
					"": `$${Numeral(expenses).format('0,0')}`
				}
			  , "total": {
					[taxPercents]: `$${Numeral(taxDeferral[property]).format('0,0')}`
				}
			}
		}
	}
)

let roiTooltip = createSelector(
	[incomeStatementByPropertyID, properties, taxDeferalByPropertyID, transactionsEx, leasesOnly, listings,  taxBracket, perfomancePeriod, startMarketDateByPropertyID, selectedProperty]
  , (incomeStatement, properties, taxDeferalByPropertyID, transactions, leasesOnly, listings, taxBracket, perfomancePeriod, startMarketDateByPropertyID, property) => {
		if(!property) property = 'total'
		let period = perfomancePeriod == 'projected' ? parsePeriod(moment().year()) : parsePeriod(perfomancePeriod)
		let currBreakdown = breakdown(properties, period.to)[property]
		let startBreakDown = breakdown(properties, period.from)[property]

		let loanPaid = (currBreakdown && currBreakdown.total && currBreakdown.total.principle || 0) - (startBreakDown && startBreakDown.total && startBreakDown.total.principle || 0)
		let netIncome = incomeStatement.netIncome[property]
		let taxDeferal = taxDeferalByPropertyID[property]
		let mediumTotal = netIncome + loanPaid + taxDeferal
		let currInvestment = investment(properties)[property]
		let roi = newRoi(properties, transactions, leasesOnly, listings, taxBracket, startMarketDateByPropertyID, perfomancePeriod)[property]
		return {
			"calc": {
				"net income": {
					"first": `${(netIncome<0 &&'-' || '')+'$'+ Numeral(Math.abs(netIncome)).format('0,0')}`
				}
			  , "loan paid": {
					"+": `$${Numeral(loanPaid).format('0,0')}`
				}
			  , "tax deferral": {
					"+": `$${Numeral(taxDeferal).format('0,0')}`
				}
			  , "total": {
					"total": `${(mediumTotal<0 &&'-' || '')+'$'+ Numeral(Math.abs(mediumTotal)).format('0,0')}`
				}
			  , "investment": {
					"/": `$${Numeral(currInvestment).format('0,0')}`
				}
			  , "annualised": {
					"total": `${Numeral(roi).format('0,0')}%`
				}

			}
		}
	}
)

let roiGainTooltip = createSelector(
	[incomeStatementByPropertyID, equityByPropertyID, taxDeferalByPropertyID, properties, transactionsEx, leasesOnly, listings, historyPrice, taxBracket, perfomancePeriod, startMarketDateByPropertyID, selectedProperty]
  , (incomeStatement, equity, taxDeferalByPropertyID, properties, transactions, leasesOnly, listings, history, taxBracket, perfomancePeriod, startMarketDateByPropertyID, property) => {
		if(!property) property = 'total'
		let period = perfomancePeriod == 'projected' ? parsePeriod(moment().year()) : parsePeriod(perfomancePeriod)
		let currBreakdown = breakdown(properties, period.to)[property]
		let startBreakDown = breakdown(properties, period.from)[property]

		let loanPaid = (currBreakdown && currBreakdown.total && currBreakdown.total.principle || 0) - (startBreakDown && startBreakDown.total && startBreakDown.total.principle || 0)
		let netIncome = incomeStatement.netIncome[property]
		let capitalGain = equity[property] && equity[property].gain
		let taxDeferal = taxDeferalByPropertyID[property]
		let mediumTotal = netIncome + loanPaid + taxDeferal + capitalGain
		let currInvestment = investment(properties)[property]
		let roi = roiWithGain(properties, transactions, leasesOnly, listings, history, taxBracket, startMarketDateByPropertyID, perfomancePeriod)[property]
		return {
			"calc": {
				"net income": {
					"first": `${(netIncome<0 &&'-' || '')+'$'+ Numeral(Math.abs(netIncome)).format('0,0')}`
				}
			  , "loan paid": {
					"+": `$${Numeral(loanPaid).format('0,0')}`
				}
			  , "tax deferral": {
					"+": `$${Numeral(taxDeferal).format('0,0')}`
				}
			  , "value gain": {
					"+": `${(capitalGain<0 &&'-' || '')+'$'+ Numeral(Math.abs(capitalGain)).format('0,0')}`
				}
			  , "total": {
					"total": `${(mediumTotal<0 &&'-' || '')+'$'+ Numeral(Math.abs(mediumTotal)).format('0,0')}`
				}
			  , "investment": {
					"/": `$${Numeral(currInvestment).format('0,0')}`
				}
			  , "annualised": {
					"total": `${Numeral(roi).format('0,0')}%`
				}

			}
		}
	}
)

let daysForRentTooltip = createSelector(
	[properties, listings, perfomancePeriod, selectedProperty]
  , (properties, listings, perfomancePeriod, property) => {
		let currdaysForRent = daysForRent(properties, listings, perfomancePeriod)
		return R.pipe(R.values, R.indexBy(R.prop('alias')), R.map(x => currdaysForRent[x._id]))(properties)
	}
)

// check leases. May be we need only terms
let occupancyRateTooltip = createSelector(
	[properties, leasesOnly, leases, perfomancePeriod, startMarketDateByPropertyID, selectedProperty]
  , (properties, leasesOnly, leases, perfomancePeriod, startMarketDateByPropertyID, property) => {
		let currdaysOccupancyRate = occupancyRate(properties, leasesOnly, leases, startMarketDateByPropertyID, perfomancePeriod)
		return R.pipe(R.values, R.indexBy(R.prop('alias')), R.map(x => Numeral(currdaysOccupancyRate[x._id]).format('0,0') + " %"))(properties)
	}
)

export let tooltipsData = (state) => ({
	'rentIncome': rentIncomeTooltip(state)
  , 'expenses': expensesTooltip(state)
  , 'currentValue': {"hint": "Estimated value based on maket state"}
  , 'valueGain': valueGainTooltip(state)
  , 'loanPrincipalPaid': loanPaidTooltip(state)
  , 'taxDeferal': taxDeferalTooltip(state)
  , 'roi': roiTooltip(state)
  , 'roiGain': roiGainTooltip(state)
  , 'daysForRent': daysForRentTooltip(state)
  , 'occupancyRate': occupancyRateTooltip(state)
})



// WEBPACK FOOTER //
// ./src/selectors/tooltip.js